import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';

import {
    careerAspirationSchema,
    hasAnyAnswer,
    toCareerAspirationRequest,
    toFormValues,
} from './careerAspirationValidation';

import type { CareerAspirationFormValues } from './careerAspirationValidation';
import type { CareerAspiration } from '@/types/career-growth';

import { useCreateCareerAspiration, useUpdateCareerAspiration } from '@/api/performance-profile';
import { useReportedWrite } from '@/hooks/utils/useReportedWrite';

/**
 * The "Aspirations de carrière" form and how it is saved, shared by every screen that asks the
 * questions: one request for the whole picture, the way the API replaces it. An existing
 * aspiration is replaced and keeps the meeting it was recorded in; otherwise a new one is recorded
 * against `thematicMeetingId` (or none). When to reset the form stays with the screen, which alone
 * knows when its answers are fresh.
 *
 * `save` resolves to whether the answers were saved, so a screen can close or stay open; a refusal
 * is already reported. A sheet with no answer at all is held until the manager confirms it in
 * `BlankAspirationConfirmDialog`, which the screen renders with `blankSaveConfirm`.
 */
export const useCareerAspirationForm = (
    employeeId: string,
    aspiration: CareerAspiration | null,
    thematicMeetingId: string | null,
) => {
    const runWrite = useReportedWrite();

    const { mutateAsync: createAspiration, isPending: isCreating } =
        useCreateCareerAspiration(employeeId);
    const { mutateAsync: updateAspiration, isPending: isUpdating } =
        useUpdateCareerAspiration(employeeId);

    const form = useForm<CareerAspirationFormValues>({
        resolver: zodResolver(careerAspirationSchema),
        defaultValues: toFormValues(aspiration),
    });

    const pendingBlankSave = useRef<((confirmed: boolean) => void) | null>(null);
    const [isBlankSaveConfirmOpen, setIsBlankSaveConfirmOpen] = useState(false);

    // A screen that goes away while the confirmation is open still settles its save, unsaved.
    useEffect(() => () => pendingBlankSave.current?.(false), []);

    const write = (values: CareerAspirationFormValues): Promise<boolean> =>
        runWrite({
            write: () =>
                aspiration
                    ? updateAspiration({
                          aspirationId: aspiration.id,
                          request: toCareerAspirationRequest(values),
                      })
                    : createAspiration({ ...toCareerAspirationRequest(values), thematicMeetingId }),
            success: 'performanceProfile.growth.aspiration.form.saved',
            failure: 'performanceProfile.growth.aspiration.form.saveError',
        });

    const save = (values: CareerAspirationFormValues): Promise<boolean> => {
        if (hasAnyAnswer(toCareerAspirationRequest(values))) {
            return write(values);
        }
        return new Promise((resolve) => {
            pendingBlankSave.current = (confirmed) => resolve(confirmed ? write(values) : false);
            setIsBlankSaveConfirmOpen(true);
        });
    };

    const settleBlankSave = (confirmed: boolean) => {
        const settle = pendingBlankSave.current;
        pendingBlankSave.current = null;
        setIsBlankSaveConfirmOpen(false);
        settle?.(confirmed);
    };

    const isSaving = isCreating || isUpdating;

    return {
        form,
        save,
        isSaving,
        blankSaveConfirm: {
            isOpen: isBlankSaveConfirmOpen,
            isSaving,
            onConfirm: () => settleBlankSave(true),
            onCancel: () => settleBlankSave(false),
        },
    };
};
